import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

// Async thunks
export const fetchRevenueTrends = createAsyncThunk(
  'analytics/fetchRevenueTrends',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.revenue.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      // Return cached data if it's less than 10 minutes old
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.revenue.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_REVENUE(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch revenue trends');
    }
  }
);

export const fetchBestSellingItems = createAsyncThunk(
  'analytics/fetchBestSellingItems',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.bestSelling.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.bestSelling.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_BEST_SELLING(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch best selling items');
    }
  }
);

export const fetchCategorySales = createAsyncThunk(
  'analytics/fetchCategorySales',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.categorySales.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.categorySales.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_CATEGORY(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch category sales');
    }
  }
);

export const fetchVegNonVegSales = createAsyncThunk(
  'analytics/fetchVegNonVegSales',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.vegNonVeg.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.vegNonVeg.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_VEG_NONVEG(restaurantId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch veg/non-veg sales');
    }
  }
);

export const fetchOrderVolumeFrequency = createAsyncThunk(
  'analytics/fetchOrderVolumeFrequency',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.orderVolume.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.orderVolume.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_ORDER_VOLUME);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order volume frequency');
    }
  }
);

export const fetchSalesPerformance = createAsyncThunk(
  'analytics/fetchSalesPerformance',
  async ({ restaurantId, groupBy = 'day' }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.analytics.salesPerformance.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.analytics.salesPerformance.data;
      }
      
      const response = await axios.get(API_ENDPOINTS.ANALYTICS_SALES_PERFORMANCE(restaurantId, groupBy));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sales performance');
    }
  }
);

export const exportAnalyticsData = createAsyncThunk(
  'analytics/exportAnalyticsData',
  async (format = 'csv', { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.ANALYTICS_EXPORT(format)}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export analytics data');
    }
  }
);

const initialState = {
  revenue: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  bestSelling: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  categorySales: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  vegNonVeg: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  orderVolume: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  salesPerformance: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  export: {
    loading: false,
    error: null,
  },
  chartData: {},
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearError: (state, action) => {
      const section = action.payload;
      if (section && state[section]) {
        state[section].error = null;
      } else {
        // Clear all errors
        Object.keys(state).forEach(key => {
          if (state[key] && state[key].error) {
            state[key].error = null;
          }
        });
      }
    },
    setChartData: (state, action) => {
      const { chartType, data } = action.payload;
      state.chartData[chartType] = data;
    },
    invalidateCache: (state, action) => {
      const section = action.payload;
      if (section && state[section]) {
        state[section].lastFetched = null;
      } else {
        // Invalidate all caches
        Object.keys(state).forEach(key => {
          if (state[key] && state[key].lastFetched) {
            state[key].lastFetched = null;
          }
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Revenue Trends
      .addCase(fetchRevenueTrends.pending, (state) => {
        state.revenue.loading = true;
        state.revenue.error = null;
      })
      .addCase(fetchRevenueTrends.fulfilled, (state, action) => {
        state.revenue.loading = false;
        state.revenue.data = action.payload;
        state.revenue.lastFetched = Date.now();
      })
      .addCase(fetchRevenueTrends.rejected, (state, action) => {
        state.revenue.loading = false;
        state.revenue.error = action.payload;
      })
      
      // Best Selling Items
      .addCase(fetchBestSellingItems.pending, (state) => {
        state.bestSelling.loading = true;
        state.bestSelling.error = null;
      })
      .addCase(fetchBestSellingItems.fulfilled, (state, action) => {
        state.bestSelling.loading = false;
        state.bestSelling.data = action.payload;
        state.bestSelling.lastFetched = Date.now();
      })
      .addCase(fetchBestSellingItems.rejected, (state, action) => {
        state.bestSelling.loading = false;
        state.bestSelling.error = action.payload;
      })
      
      // Category Sales
      .addCase(fetchCategorySales.pending, (state) => {
        state.categorySales.loading = true;
        state.categorySales.error = null;
      })
      .addCase(fetchCategorySales.fulfilled, (state, action) => {
        state.categorySales.loading = false;
        state.categorySales.data = action.payload;
        state.categorySales.lastFetched = Date.now();
      })
      .addCase(fetchCategorySales.rejected, (state, action) => {
        state.categorySales.loading = false;
        state.categorySales.error = action.payload;
      })
      
      // Veg/Non-Veg Sales
      .addCase(fetchVegNonVegSales.pending, (state) => {
        state.vegNonVeg.loading = true;
        state.vegNonVeg.error = null;
      })
      .addCase(fetchVegNonVegSales.fulfilled, (state, action) => {
        state.vegNonVeg.loading = false;
        state.vegNonVeg.data = action.payload;
        state.vegNonVeg.lastFetched = Date.now();
      })
      .addCase(fetchVegNonVegSales.rejected, (state, action) => {
        state.vegNonVeg.loading = false;
        state.vegNonVeg.error = action.payload;
      })
      
      // Order Volume Frequency
      .addCase(fetchOrderVolumeFrequency.pending, (state) => {
        state.orderVolume.loading = true;
        state.orderVolume.error = null;
      })
      .addCase(fetchOrderVolumeFrequency.fulfilled, (state, action) => {
        state.orderVolume.loading = false;
        state.orderVolume.data = action.payload;
        state.orderVolume.lastFetched = Date.now();
      })
      .addCase(fetchOrderVolumeFrequency.rejected, (state, action) => {
        state.orderVolume.loading = false;
        state.orderVolume.error = action.payload;
      })
      
      // Sales Performance
      .addCase(fetchSalesPerformance.pending, (state) => {
        state.salesPerformance.loading = true;
        state.salesPerformance.error = null;
      })
      .addCase(fetchSalesPerformance.fulfilled, (state, action) => {
        state.salesPerformance.loading = false;
        state.salesPerformance.data = action.payload;
        state.salesPerformance.lastFetched = Date.now();
      })
      .addCase(fetchSalesPerformance.rejected, (state, action) => {
        state.salesPerformance.loading = false;
        state.salesPerformance.error = action.payload;
      })
      
      // Export Analytics
      .addCase(exportAnalyticsData.pending, (state) => {
        state.export.loading = true;
        state.export.error = null;
      })
      .addCase(exportAnalyticsData.fulfilled, (state) => {
        state.export.loading = false;
      })
      .addCase(exportAnalyticsData.rejected, (state, action) => {
        state.export.loading = false;
        state.export.error = action.payload;
      });
  },
});

// Selectors
export const selectRevenueData = (state) => state.analytics.revenue;
export const selectBestSellingData = (state) => state.analytics.bestSelling;
export const selectCategorySalesData = (state) => state.analytics.categorySales;
export const selectVegNonVegData = (state) => state.analytics.vegNonVeg;
export const selectOrderVolumeData = (state) => state.analytics.orderVolume;
export const selectSalesPerformanceData = (state) => state.analytics.salesPerformance;
export const selectExportStatus = (state) => state.analytics.export;
export const selectChartData = (state) => state.analytics.chartData;

export const selectAnalyticsLoading = (state) => {
  const sections = ['revenue', 'bestSelling', 'categorySales', 'vegNonVeg', 'orderVolume', 'salesPerformance'];
  return sections.some(section => state.analytics[section]?.loading);
};

export const selectAnalyticsErrors = (state) => {
  const sections = ['revenue', 'bestSelling', 'categorySales', 'vegNonVeg', 'orderVolume', 'salesPerformance'];
  return sections
    .map(section => ({ section, error: state.analytics[section]?.error }))
    .filter(({ error }) => error);
};

export const { clearError, setChartData, invalidateCache } = analyticsSlice.actions;
export default analyticsSlice.reducer;

